import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";

const STATUS_META: Record<string, { label: string; color: string; order: number }> = {
  present: { label: "Present", color: "#16a34a", order: 0 },
  absent: { label: "Absent", color: "#dc2626", order: 1 },
  half_day: { label: "Half Day", color: "#f59e0b", order: 2 },
  leave: { label: "On Leave", color: "#4f46e5", order: 3 },
  holiday: { label: "Holiday", color: "#0d9488", order: 4 },
  weekly_off: { label: "Weekly Off", color: "#a8a29e", order: 5 },
  week_off_worked: { label: "Week Off Worked", color: "#0891b2", order: 6 },
  on_duty: { label: "On Duty", color: "#b45309", order: 7 },
  not_marked: { label: "Not Marked", color: "#d6d3d1", order: 8 },
};
const FALLBACK_COLORS = ["#0d9488", "#8b5cf6", "#bb1656", "#6e6d7a"];

/** The tenant's attendance mix for the current period, donut + legend - same
 * real `attendance_mix` field the previous dashboard already showed as a
 * grid of badges (MonthlySummaryReport on the backend), just restyled. */
export function AttendanceDonut({ mix }: { mix: Record<string, number> }) {
  const entries = Object.entries(mix).filter(([, count]) => count > 0);
  const total = entries.reduce((sum, [, count]) => sum + count, 0);
  const present = mix.present ?? 0;

  const rows = entries
    .map(([status, count], i) => ({
      status,
      count,
      label: STATUS_META[status]?.label ?? status.replace(/_/g, " "),
      color: STATUS_META[status]?.color ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length]!,
      order: STATUS_META[status]?.order ?? 99,
    }))
    .sort((a, b) => a.order - b.order);

  if (total === 0) {
    return <p className="flex h-56 items-center justify-center text-sm text-slate-400">No attendance recorded for this period yet.</p>;
  }

  return (
    <div className="flex flex-col items-center gap-5 sm:flex-row">
      <div className="relative h-48 w-48 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          {/* accessibilityLayer=false + Pie's own rootTabIndex=-1 - see
              DepartmentDonut.tsx's full explanation. Both default tabIndex
              paths (chart root <svg> and Pie's own wrapping <g>) draw an
              unstyled black browser focus rectangle when clicked; the
              legend list beside this chart already carries the same data as
              accessible HTML, so disabling both is safe. */}
          <PieChart accessibilityLayer={false}>
            <Pie
              data={rows}
              dataKey="count"
              nameKey="label"
              innerRadius={55}
              outerRadius={80}
              paddingAngle={2}
              // A real but small category (e.g. one person "On Leave" out
              // of a large headcount) would otherwise render as a near-
              // invisible sliver, easy to mistake for a rendering glitch
              // rather than genuine data - a minimum angle keeps every
              // present category visibly a wedge.
              minAngle={3}
              strokeWidth={0}
              rootTabIndex={-1}
            >
              {rows.map((r) => (
                <Cell key={r.status} fill={r.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <p className="text-2xl font-semibold text-slate-900">{present}</p>
          <p className="text-xs text-slate-500">Present</p>
        </div>
      </div>
      <ul className="w-full min-w-0 flex-1 space-y-2">
        {rows.map((r) => (
          <li key={r.status} className="flex items-center justify-between gap-2 text-sm">
            <span className="flex min-w-0 items-center gap-2 text-slate-600">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: r.color }} />
              <span className="truncate">{r.label}</span>
            </span>
            <span className="shrink-0 font-medium text-slate-800">
              {r.count} <span className="text-slate-400">({((r.count / total) * 100).toFixed(1)}%)</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
